import { useRef, useEffect, SyntheticEvent, useState } from 'react'
import { AppManager, UserManager } from '@app/managers'
import { useUserData } from '@app/data'
import {
  Button,
  LinearBottom,
  MarketingDrawer,
  SnackBar,
} from '@app/components/general'
import { useRouter } from 'next/router'
import { metaSetter } from '@app/utils'
import type { PageProps } from '@app/types'
import { Header, Header2, Header3 } from '@app/components/general/header'
import { FormControl } from '@app/components/general/form-control'
import { TextField } from '@app/components/general/text-field'
import { classNames } from '@app/utils/classes'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { Paragraph } from '@app/components/stateless/typo/paragraph'
import { AppLayout } from '@app/components/app-layout'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import { I18nDictionary } from 'next-translate'

export const getStaticProps: GetStaticProps<{
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
}> = async (ctx) => {
  return {
    props: await loadNamespaces({
      ...ctx,
      pathname: '/reset-password',
    }),
  }
}

function ResetPassword({ name }: PageProps) {
  const router = useRouter()
  const {
    loading,
    forgotPassword,
    forgotPasswordData,
    resetPassword,
    resetPasswordData,
  } = useUserData(true)

  const emailRef = useRef<HTMLInputElement>(null)
  const resetRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation('reset-password')

  const [emailState, setEmailState] = useState<string>('')
  const [resetState, setResetState] = useState<string>('')

  const resetSent = forgotPasswordData?.forgotPassword?.email == 'true'
  const title = t(resetSent ? 'enter-reset' : 'header')

  useEffect(() => {
    if (resetPasswordData?.resetPassword?.jwt) {
      UserManager.setUser(resetPasswordData.resetPassword)
      ;(async () => {
        await router.push('/dashboard')
      })()
    }
  }, [router, resetPasswordData])

  useEffect(() => {
    if (resetSent) {
      AppManager.toggleSnack(true, t('reset-sent'), 'message')
    }
  }, [resetSent, t])

  const onEmailChange = (
    e: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    e?.preventDefault()
    setEmailState(e?.currentTarget?.value)
  }

  const onResetEvent = (
    e: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    e?.preventDefault()
    setResetState(e?.currentTarget?.value)
  }

  const submit = async (e: SyntheticEvent) => {
    e?.preventDefault()
    try {
      if (resetSent && resetRef?.current?.value) {
        await resetPassword({
          variables: {
            email: emailState,
            resetCode: resetState,
          },
        })
      } else if (emailRef?.current?.value) {
        await forgotPassword({
          variables: {
            email: emailState,
          },
        })
      }
    } catch (e) {
      console.error(e)
    }
  }

  const FormInputRender = () => {
    if (resetSent) {
      return (
        <>
          <FormControl htmlFor='resetCode'>{t('enter-reset')}</FormControl>

          <TextField
            id='resetCode'
            aria-describedby='my-reset-text'
            placeholder={t('reset-code')}
            type='text'
            autoFocus
            value={resetState}
            required
            ref={resetRef}
            onChange={onResetEvent}
          />
        </>
      )
    }

    return (
      <>
        <FormControl htmlFor='email'>{t('auth:email')}</FormControl>
        <TextField
          id='email'
          aria-describedby='my-email-text'
          placeholder={t('auth:email')}
          type='email'
          autoFocus
          onChange={onEmailChange}
          autoComplete='email'
          required
          value={emailState}
          ref={emailRef}
        />
      </>
    )
  }

  const FormRender = () => {
    return (
      <form
        autoComplete={resetSent ? 'on' : 'off'}
        onSubmit={submit}
        className='flex flex-col space-y-6'
      >
        <FormInputRender />
        <Button
          className={classNames(
            `border-2 rounded-full py-2 px-3 text-base`,
            !resetSent && emailState.length < 5
              ? 'border-gray-700'
              : 'border-blue-700 text-blue-600'
          )}
          type='submit'
        >
          {t(resetSent ? 'submit' : 'send-email')}
        </Button>
      </form>
    )
  }

  return (
    <MarketingDrawer title={name}>
      <div className='container mx-auto text-center'>
        <div className='py-2 flex flex-col place-items-center space-y-8'>
          <Header>{title}</Header>
          <FormRender />
          <div className='py-8'>
            <div className='py-4 border px-8 rounded text-left'>
              <Header2>{t('subheader')}</Header2>
              <Paragraph>{t('subheader-d')}</Paragraph>
              <div className='py-2'>
                <Header3>{t('issue-reset')}</Header3>
                <Paragraph>{t('issue-reset-d')}</Paragraph>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LinearBottom loading={loading} />
      <SnackBar topLevel />
    </MarketingDrawer>
  )
}

ResetPassword.getLayout = AppLayout

export default metaSetter(
  { ResetPassword },
  {
    translation: 'reset-password',
    gql: true,
  }
)
